import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ASSESSMENTS = [
  { id: 'intimacy_health',         title: 'Intimate Wellness Snapshot',    icon: 'fa-heart',           desc: 'Overall intimate health baseline',   palette: '',             color: '#E8B4B8', section: 'women' },
  { id: 'pelvic_floor',            title: 'Pelvic Floor Self-Check',        icon: 'fa-shield-halved',   desc: 'Symptoms, awareness, function',      palette: 'palette-teal', color: '#4FB3A9', section: 'women' },
  { id: 'hormonal_balance',        title: 'Cycle & Hormone Insight',        icon: 'fa-scale-balanced',  desc: 'Hormonal patterns & indicators',     palette: '',             color: '#D4AF37', section: 'women' },
  { id: 'sexual_confidence',       title: 'Intimacy Confidence Index',      icon: 'fa-star',            desc: 'Emotional & relational dimensions',  palette: 'palette-rose', color: '#E8B4B8', section: 'women' },
  { id: 'relationship_intimacy',   title: 'Relationship Intimacy',          icon: 'fa-users',           desc: 'Connection, communication, desire',  palette: '',             color: '#67C9C0', section: 'women' },
  { id: 'menopause_wellness',      title: 'Menopause Readiness',            icon: 'fa-spa',             desc: 'Perimenopause / menopause mapping',  palette: 'palette-rose', color: '#D4AF37', section: 'women' },
  { id: 'postpartum_recovery',     title: 'Postpartum Recovery Score',      icon: 'fa-baby',            desc: 'Fourth trimester & beyond',          palette: 'palette-rose', color: '#E8B4B8', section: 'women' },
  { id: 'pmos_wellness',           title: 'PMOS Wellness Index',            icon: 'fa-bolt',            desc: 'Hormones · metabolism · mood',       palette: '',             color: '#D4AF37', section: 'women' },
  { id: 'male_sexual_wellness',    title: "Men's Sexual Wellness",          icon: 'fa-mars',            desc: 'Erection · libido · performance',    palette: '',             color: '#D4AF37', section: 'men' },
  { id: 'male_prostate_hormonal',  title: 'Prostate & Hormonal Wellness',   icon: 'fa-heart-pulse',     desc: 'Prostate · testosterone · screening', palette: 'palette-teal', color: '#4FB3A9', section: 'men' },
  { id: 'male_stamina_endurance',  title: 'Stamina & Endurance Index',      icon: 'fa-bolt',            desc: 'Cardio · recovery · endurance',      palette: '',             color: '#D4AF37', section: 'men' },
  { id: 'male_sleep_recovery',     title: 'Sleep & Recovery Score',         icon: 'fa-moon',            desc: 'Sleep · testosterone · recovery',    palette: 'palette-teal', color: '#4FB3A9', section: 'men' },
  { id: 'male_mental_resilience',  title: 'Mental Resilience & Stress',     icon: 'fa-brain',           desc: 'Stress · regulation · burnout',      palette: 'palette-rose', color: '#E8B4B8', section: 'men' },
  { id: 'male_couple_intimacy',    title: 'Couple Intimacy (Him)',          icon: 'fa-people-arrows',   desc: 'Connection · communication · trust', palette: 'palette-rose', color: '#E8B4B8', section: 'men' },
  { id: 'male_premature_ejaculation', title: 'Ejaculatory Control Index',   icon: 'fa-clock',           desc: 'Latency · anxiety · technique',      palette: '',             color: '#D4AF37', section: 'men' },
  { id: 'male_andropause',         title: 'Andropause / Mid-life',          icon: 'fa-sun',             desc: 'Testosterone · vitality · midlife',  palette: '',             color: '#D4AF37', section: 'men' },
];

const tierColors = { low: '#ef4444', mid_low: '#f97316', mid: '#eab308', mid_high: '#22c55e', high: '#10b981' };
const tierLabels = { low: 'Needs Attention', mid_low: 'Building', mid: 'Developing', mid_high: 'Thriving', high: 'Excellent' };

export default function Assessments() {
  const { authHeader, user } = useAuth();
  const firstName = (user?.name || '').split(' ')[0] || '';
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // list | quiz | results
  const [assessSection, setAssessSection] = useState('women');
  const [current, setCurrent] = useState(null); // current assessment config
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [myResults, setMyResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/assessments/my-results`, { headers: authHeader() }).then(r => setMyResults(r.data.results || [])).catch(() => {});
  }, []);

  const startAssessment = async (a) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/assessments/${a.id}`, { headers: authHeader() });
      setQuestions(r.data.questions || []);
      setCurrent(a);
      setQIdx(0);
      setAnswers({});
      setView('quiz');
    } catch { toast.error('Could not load assessment.'); }
    finally { setLoading(false); }
  };

  const selectAnswer = async (optionIdx) => {
    const q = questions[qIdx];
    const newAnswers = { ...answers, [q.id]: optionIdx };
    setAnswers(newAnswers);
    setTimeout(async () => {
      if (qIdx < questions.length - 1) { setQIdx(i => i + 1); }
      else { await submitAssessment(newAnswers); }
    }, 320);
  };

  const submitAssessment = async (finalAnswers) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/assessments/submit`,
        { assessment_type: current.id, answers: finalAnswers },
        { headers: authHeader() }
      );
      setResults(r.data);
      setView('results');
      axios.get(`${API}/api/assessments/my-results`, { headers: authHeader() }).then(r => setMyResults(r.data.results || [])).catch(() => {});
    } catch { toast.error('Could not submit assessment.'); }
    finally { setLoading(false); }
  };

  const progress = questions.length ? ((qIdx) / questions.length) * 100 : 0;

  if (view === 'quiz' && questions.length > 0) {
    const q = questions[qIdx];
    return (
      <div className="assessment-shell">
        <div className="assess-progress">
          <div className="assess-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <button onClick={() => setView('list')} style={{ position: 'absolute', top: 12, left: 16, background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '0.93rem', zIndex: 10 }}>
          <i className="fa-solid fa-xmark" />
        </button>

        <AnimatePresence mode="wait">
          <motion.div key={qIdx} className="assess-question-screen"
            initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.25 }}>
            <div className="assess-q-num">{firstName ? `${firstName} · ` : ''}{current?.title} · Q{qIdx + 1} of {questions.length}</div>
            <div className="assess-q-text">{q.text}</div>
            <div>
              {q.options.map((opt, i) => (
                <button key={i} className={`assess-option${answers[q.id] === i ? ' selected' : ''}`}
                  onClick={() => selectAnswer(i)} data-testid={`option-${i}`}>
                  <span className="assess-option-letter">{String.fromCharCode(65 + i)}</span>
                  {opt}
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  if (view === 'results' && results) {
    const color = tierColors[results.tier] || 'var(--gold)';
    const circumference = 2 * Math.PI * 54;
    const offset = circumference - (results.score / 100) * circumference;
    const tierLabel = results.tier_label || tierLabels[results.tier] || '';
    return (
      <motion.div className="assessment-shell" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="results-screen">
          <div className="eyebrow">Assessment Complete</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.25rem', lineHeight: 1.2, marginBottom: 26 }}>
            {firstName ? <>{firstName}, your </> : 'Your '}
            <span style={{ color, fontStyle: 'italic' }}>{tierLabel}</span> Score
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 28 }}>
            <div className="score-ring-wrap">
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="54" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="10" />
                <circle cx="70" cy="70" r="54" fill="none" stroke={color} strokeWidth="10"
                  strokeDasharray={circumference} strokeDashoffset={offset}
                  strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1.2s ease' }} />
              </svg>
              <div className="score-number">
                <span style={{ fontFamily: 'var(--font-ui)', fontSize: '2.7rem', fontWeight: 700, color }}>{results.score}</span>
                <span style={{ fontSize: '0.73rem', color: 'var(--text-dim)', letterSpacing: '0.1em' }}>/100</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.76rem', padding: '4px 12px', background: 'rgba(212,175,55,0.1)', color: 'var(--gold)', display: 'inline-block', marginBottom: 10, letterSpacing: '0.15em', fontWeight: 700 }}>
                {tierLabel?.toUpperCase()}
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.35rem', lineHeight: 1.25 }}>{current?.title}</div>
            </div>
          </div>

          {/* Detailed narrative summary */}
          {results.detailed_summary && (
            <div style={{
              marginBottom: 22,
              padding: '18px 18px',
              background: 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(10,10,10,0)), var(--bg2)',
              border: '1px solid rgba(212,175,55,0.18)',
              position: 'relative',
            }}>
              <div className="eyebrow" style={{ marginBottom: 10 }}>What This Means</div>
              <p style={{ fontFamily: 'var(--font-ui)', fontSize: '1.04rem', lineHeight: 1.72, color: 'rgba(255,255,255,0.84)' }}>
                {results.detailed_summary}
              </p>
            </div>
          )}

          {/* Insights */}
          <div style={{ marginBottom: 22 }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>Key Insights</div>
            {(results.insights || []).map((ins, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-start' }}>
                <i className="fa-solid fa-circle" style={{ color: 'var(--gold)', fontSize: '0.45rem', marginTop: 8, flexShrink: 0 }} />
                <p style={{ fontSize: '1.03rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>{ins}</p>
              </div>
            ))}
          </div>

          {/* Strengths */}
          {(results.strengths || []).length > 0 && (
            <div style={{ marginBottom: 22, borderTop: '1px solid var(--border-soft)', paddingTop: 18 }}>
              <div className="eyebrow" style={{ marginBottom: 10, color: '#10b981' }}>Your Strengths</div>
              {results.strengths.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                  <i className="fa-solid fa-check" style={{ color: '#10b981', fontSize: '0.81rem', marginTop: 5, flexShrink: 0 }} />
                  <p style={{ fontSize: '0.93rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>{s}</p>
                </div>
              ))}
            </div>
          )}

          {/* Focus Areas */}
          {(results.focus_areas || []).length > 0 && (
            <div style={{ marginBottom: 22, borderTop: '1px solid var(--border-soft)', paddingTop: 18 }}>
              <div className="eyebrow" style={{ marginBottom: 10, color: '#E8B4B8' }}>Where to Focus Next</div>
              {results.focus_areas.map((f, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                  <i className="fa-solid fa-arrow-right" style={{ color: '#E8B4B8', fontSize: '0.81rem', marginTop: 6, flexShrink: 0 }} />
                  <p style={{ fontSize: '0.93rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>{f}</p>
                </div>
              ))}
            </div>
          )}

          <div style={{ borderTop: '1px solid var(--border-soft)', paddingTop: 18, marginBottom: 20 }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>Recommendations</div>
            {(results.recommendations || []).map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                <span style={{ fontFamily: 'var(--font-ui)', fontSize: '0.77rem', color: 'var(--gold)', minWidth: 22, marginTop: 4, fontWeight: 700, letterSpacing: '0.05em' }}>0{i + 1}</span>
                <p style={{ fontSize: '0.93rem', color: 'var(--text-muted)', lineHeight: 1.65 }}>{r}</p>
              </div>
            ))}
          </div>

          {/* Next-step CTAs */}
          <div style={{ borderTop: '1px solid var(--border-soft)', paddingTop: 18, marginBottom: 18 }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>Your Next Step</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <a
                href="https://www.intiviv.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { axios.post(`${API}/api/track`, { event: 'assessment_cta', label: 'store', meta: { assessment_id: current?.id } }, { headers: authHeader() }).catch(() => {}); }}
                data-testid="result-cta-store"
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: 8, padding: '18px 10px',
                  background: 'linear-gradient(135deg, rgba(212,175,55,0.12), rgba(10,10,10,0)), var(--bg2)',
                  border: '1px solid var(--gold)',
                  textDecoration: 'none',
                  textAlign: 'center',
                  fontFamily: 'var(--font-ui)',
                  cursor: 'pointer',
                }}
              >
                <i className="fa-solid fa-bag-shopping" style={{ color: 'var(--gold)', fontSize: '1.7rem' }} />
                <div style={{ fontSize: '0.94rem', fontWeight: 700, color: 'var(--text)', letterSpacing: '0.05em' }}>INTIVIV Store</div>
                <div style={{ fontSize: '0.77rem', color: 'var(--text-dim)', letterSpacing: '0.05em' }}>Curated Products</div>
              </a>
              <button
                type="button"
                onClick={() => {
                  axios.post(`${API}/api/track`, { event: 'assessment_cta', label: 'consult', meta: { assessment_id: current?.id } }, { headers: authHeader() }).catch(() => {});
                  navigate('/consult');
                }}
                data-testid="result-cta-consult"
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: 8, padding: '18px 10px',
                  background: 'linear-gradient(135deg, rgba(37,211,102,0.14), rgba(10,10,10,0)), var(--bg2)',
                  border: '1px solid #25D366',
                  textAlign: 'center',
                  fontFamily: 'var(--font-ui)',
                  cursor: 'pointer',
                  color: 'var(--text)',
                }}
              >
                <i className="fa-brands fa-whatsapp" style={{ color: '#25D366', fontSize: '1.85rem' }} />
                <div style={{ fontSize: '0.94rem', fontWeight: 700, letterSpacing: '0.05em' }}>Book Consult</div>
                <div style={{ fontSize: '0.77rem', color: 'var(--text-dim)', letterSpacing: '0.05em' }}>Dr. Vikram on WhatsApp</div>
              </button>
            </div>
          </div>

          <button className="btn-gold w-full" onClick={() => setView('list')} style={{ marginBottom: 10, fontSize: '0.92rem' }}>
            <i className="fa-solid fa-arrow-left" /> Back to Assessments
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className={`assessment-list-shell theme-${assessSection}`} style={{ minHeight: '100vh', paddingBottom: 'var(--bottom-h)', position: 'relative' }}>
      <div className="education-aurora" aria-hidden="true" />
      <div style={{ padding: '18px 16px 8px', position: 'relative', zIndex: 1 }}>
        <div className="eyebrow">{firstName ? `Hello, ${firstName}` : 'Wellness Assessments'}</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.5rem', lineHeight: 1.18, marginBottom: 6 }}>Know Your <span className="gold-italic">Score</span></div>
        <p style={{ fontFamily: 'var(--font-ui)', fontSize: '1.01rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.58 }}>{firstName ? `${firstName}, ` : ''}16 clinically-designed assessments · 15 questions each · ~4 minutes</p>
      </div>

      {/* Section toggle — Women / Men */}
      <div className="section-toggle" style={{ position: 'relative', zIndex: 1 }}>
        <button
          className={`section-tab ${assessSection === 'women' ? 'active' : ''}`}
          onClick={() => setAssessSection('women')}
          data-testid="assess-section-women"
        >For Her</button>
        <button
          className={`section-tab ${assessSection === 'men' ? 'active' : ''}`}
          onClick={() => setAssessSection('men')}
          data-testid="assess-section-men"
        >For Him</button>
      </div>

      {/* Past results row */}
      {myResults.length > 0 && (
        <div style={{ marginBottom: 16, marginTop: 16, position: 'relative', zIndex: 1 }}>
          <div className="eyebrow" style={{ padding: '0 16px', marginBottom: 10 }}>Your Latest Scores</div>
          <div className="scroll-row" data-testid="my-scores-scroll" style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
            {myResults.map(r => (
              <div key={r.id} style={{ flexShrink: 0, padding: '14px 16px', background: 'var(--bg2)', border: '1px solid var(--border-soft)', minWidth: 140, textAlign: 'center', scrollSnapAlign: 'start' }} data-testid={`my-score-${r.assessment_type}`}>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: tierColors[r.tier], lineHeight: 1, marginBottom: 6 }}>{r.score}</div>
                <div style={{ fontSize: '0.76rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', lineHeight: 1.45 }}>{r.assessment_type?.replace(/_/g, ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="assess-grid" style={{ padding: '14px 16px 0', position: 'relative', zIndex: 1 }}>
        {ASSESSMENTS.filter(a => a.section === assessSection).map((a) => (
          <motion.button key={a.id} onClick={() => startAssessment(a)} disabled={loading}
            className="assess-card immersive-card"
            whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
            data-testid={`assess-${a.id}`}
          >
            <i className={`fa-solid ${a.icon}`} style={{ color: a.color, fontSize: '1.7rem', display: 'block', marginBottom: 10, filter: `drop-shadow(0 0 8px ${a.color}66)` }} />
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.24rem', fontWeight: 500, color: 'rgba(255,255,255,0.96)', lineHeight: 1.28, marginBottom: 6, letterSpacing: '0.005em' }}>{a.title}</div>
            <div style={{ fontFamily: 'var(--font-ui)', fontSize: '0.87rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.45, letterSpacing: '0.01em' }}>{a.desc}</div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
